import axios, { AxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '@/constants/api-endpoint';
import { APIResponse } from '@/types/res';
import { forceLogout, isRevokedAuthError, sessionEndedMessage } from '@/utils/utils';

const API_URL = process.env.NEXT_PUBLIC_BASE_API_URL;

// Tạo instance của Axios
export const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

// Axios Request Interceptor
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

let refreshPromise: Promise<string> | null = null;

// Một promise chung: hai request cùng 401 gọi song song bằng một refresh token thì cái sau bị coi là
// tái sử dụng và giết cả phiên. Axios trần vì qua apiClient sẽ rơi ngược vào interceptor 401 này.
export const refreshAccessToken = (): Promise<string> => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }
            const { data } = await axios.post(`${API_URL}${API_ENDPOINTS.REFRESH_TOKEN}`, { refresh_token: refreshToken });
            if (!data?.data?.access_token || !data?.data?.refresh_token) {
                throw new Error('Phản hồi gia hạn thiếu token');
            }
            localStorage.setItem('token', data.data.access_token);
            localStorage.setItem('refresh_token', data.data.refresh_token);
            return data.data.access_token as string;
        })().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

// Axios Response Interceptor (có thể dùng để refresh token)
apiClient.interceptors.response.use(
    (response) => {
        if (response?.data.success !== undefined) {
            if (response.data.success) {
                return response
            } else {
                return Promise.reject({
                    message: response.data.message || 'Yêu cầu không thành công',
                    code: response.data.code,
                    response,
                });
            }
        }
        if (response.data != "OK"){
            return response.data
        } else {
            return response;
        }
    },
    async (error) => {
        const originalRequest = error.config;

        const endedReason = sessionEndedMessage(error);
        if (endedReason) {
            forceLogout(endedReason);
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return apiClient(originalRequest);
            } catch (err) {
                if (isRevokedAuthError(err) || !localStorage.getItem('refresh_token')) {
                    forceLogout(sessionEndedMessage(err) || undefined);
                }
                return Promise.reject(err);
            }
        }
        return Promise.reject(error);
    }
);

const handleError = (error: any) => {
    if (error.code === 'ECONNABORTED') {
        throw new Error('Hết thời gian yêu cầu: máy chủ không phản hồi kịp thời!');
    }
    if (error.response) {
        throw error.response.data.message;
    } else if (error.request) {
        throw new Error(' Không có phản hồi từ máy chủ!');
    } else {
        throw new Error(' Lỗi thiết lập yêu cầu!');
    }
};

// Method GET
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiClient.get(url, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method POST
export const post = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiClient.post(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method PUT
export const put = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiClient.put(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method PATCH
export const patch = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiClient.patch(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method DELETE
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiClient.delete(url, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method HEAD
export const head = async (url: string, config?: AxiosRequestConfig) => {
    try {
        return await apiClient.head(url, config);
    } catch (error) {
        throw handleError(error);
    }
};